import { LockedNote, requireFullAccess, useFullAccess } from "../../billing/access";
/** Ücretsiz katman: donatı seçimi/kapasite sonuçları tasarım çıktısıdır */
const LockedReinforcement = (): JSX.Element => <LockedNote className="m-2" what={t("Donatı tasarımı")} />;
import { t } from "../../i18n";
import { useEffect, useState } from "react";
import type { WallInput } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { Tabs } from "../../ui/Tabs";
import { GeometryTab } from "./GeometryTab";
import { BackfillTab } from "./BackfillTab";
import { FoundationTab } from "./FoundationTab";
import { WaterTab } from "./WaterTab";
import { LoadsTab } from "./LoadsTab";
import { SeismicTab } from "./SeismicTab";
import { MaterialTab } from "./MaterialTab";
import { ChecksTab } from "./ChecksTab";
import { GlobalStabilityTab } from "./GlobalStabilityTab";
import { RcMaterialTab } from "./RcMaterialTab";
import { ReinforcementTab } from "./ReinforcementTab";
import { QuantitiesTab } from "./QuantitiesTab";
import { DrawingTab } from "./DrawingTab";
import { SoilReportDialog } from "../extract/SoilReportDialog";
import { useExtractCapabilities } from "../extract/useExtractCapabilities";
import { useHasApi } from "../../auth/useAuth";
import { useCadUi } from "../../state/cadUiStore";
import { CadFormContext } from "../../ui/formContext";

export type Update = (recipe: (w: WallInput) => void) => void;
export interface TabProps { wall: WallInput; update: Update }

/**
 * Girdi adımları (Cem 2026-09-29 sadeleştirme): sol palet yalnız hesap girdilerini sırayla taşır — metraj girdileri Metraj paftasında,
 * çizim/pafta ayarları Çizim paftasında. CAD görünümünde numaralı adım çipleri + Önceki / Sonraki.
 */
const TABS = [
  { id: "geometry", label: t("Geometri") }, { id: "backfill", label: t("Dolgu") }, { id: "foundation", label: t("Temel") }, { id: "water", label: "Su" },
  { id: "loads", label: t("Yükler") }, { id: "seismic", label: t("Deprem") }, { id: "material", label: t("Malzeme") }, { id: "checks", label: t("Kontroller") },
  { id: "globalStability", label: t("Toptan göçme") },
];
const RC_TABS = [...TABS.slice(0, 6), { id: "material", label: t("Beton · Çelik") }, { id: "reinforcement", label: t("Donatı") }, ...TABS.slice(7)];

const PANELS: Record<string, (p: TabProps) => JSX.Element> = {
  geometry: GeometryTab, backfill: BackfillTab, foundation: FoundationTab, water: WaterTab, loads: LoadsTab, seismic: SeismicTab, material: MaterialTab, checks: ChecksTab,
  globalStability: GlobalStabilityTab, reinforcement: ReinforcementTab, quantities: QuantitiesTab, drawing: DrawingTab,
};

export function WallEditor({ wall, variant = "paper" }: { wall: WallInput; variant?: "paper" | "cad" }): JSX.Element {
  // CAD kabuğunda sekme durumu paylaşımlı (komut satırı ve şerit de değiştirir); kâğıt görünümünde yerel
  const [localTab, setLocalTab] = useState("geometry");
  const cadTab = useCadUi((s) => s.propTab), setCadTab = useCadUi((s) => s.setPropTab);
  const cadDialog = useCadUi((s) => s.dialog), setCadDialog = useCadUi((s) => s.setDialog);
  const tab = variant === "cad" ? cadTab : localTab, setTab = variant === "cad" ? setCadTab : setLocalTab;
  const updateWall = useProjectStore((s) => s.updateWall);
  const update: Update = (recipe) => updateWall(wall.id, recipe);
  const props: TabProps = { wall, update };
  const isRc = wall.geometry.mode === "rc-cantilever";
  const tabs = isRc ? RC_TABS : TABS;
  const active = tabs.some((x) => x.id === tab) ? tab : "geometry";
  const full = useFullAccess();
  const Panel = active === "reinforcement" && !full ? LockedReinforcement : active === "material" && isRc ? RcMaterialTab : PANELS[active] ?? GeometryTab;
  // Zemin etüt raporundan al: her zaman (yerel okuyucu); API + Gemini anahtarı varsa yapay zekâ seçeneği de çıkar
  const hasApi = useHasApi();
  const loadCaps = useExtractCapabilities((s) => s.load);
  useEffect(() => { if (hasApi) void loadCaps(); }, [hasApi, loadCaps]);
  const [soilDlg, setSoilDlg] = useState(false);
  const soilTab = active === "backfill" || active === "foundation" || active === "water" || active === "seismic";
  return (
    <CadFormContext.Provider value={variant === "cad"}>
    <div className={`flex flex-col h-full ${variant === "cad" ? "cad-props" : ""}`}>
      {variant === "cad" ? (
        <div role="tablist" aria-label={t("Girdi bölümleri")} className="flex flex-wrap gap-px p-[3px] bg-cad-sub border-b border-cad-line shrink-0">
          {tabs.map((x, i) => <button key={x.id} role="tab" type="button" aria-selected={x.id === active} aria-controls={`panel-${x.id}`} onClick={() => setTab(x.id)} className="cad-chip"><span aria-hidden className="mr-1 font-mono text-[10px] opacity-60">{i + 1}</span>{x.label}</button>)}
          {soilTab ? <button type="button" className="cad-chip ml-auto" onClick={() => { if (requireFullAccess(t("Zemin raporundan veri okuma"))) setSoilDlg(true); }}>✨ {t("Zemin etüt raporundan al")}</button> : null}
        </div>
      ) : (
        <div className="px-3 pt-3 flex items-start gap-2 flex-wrap">
          <Tabs tabs={tabs} active={active} onChange={setTab} size="sm" label={t("Girdi bölümleri")} />
          {soilTab ? <button type="button" className="btn-ghost text-xs ml-auto" onClick={() => { if (requireFullAccess(t("Zemin raporundan veri okuma"))) setSoilDlg(true); }}>✨ {t("Zemin etüt raporundan al")}</button> : null}
        </div>
      )}
      {soilDlg || (variant === "cad" && cadDialog === "soil") ? <SoilReportDialog open onClose={() => { setSoilDlg(false); if (cadDialog === "soil") setCadDialog(null); }} wall={wall} /> : null}
      <div id={`panel-${active}`} role="tabpanel" className={`${variant === "cad" ? "cad-props-body" : "p-3 gap-3"} grid overflow-auto`}>
        <Panel {...props} />
      </div>
      {variant === "cad" ? (() => {
        const i = tabs.findIndex((x) => x.id === active), prev = tabs[i - 1], next = tabs[i + 1];
        return (
          <div className="shrink-0 flex items-center gap-1 px-1.5 py-1 border-t border-cad-line bg-cad-sub text-[11px]">
            {prev ? <button type="button" className="cad-btn-box" onClick={() => setTab(prev.id)}>← {prev.label}</button> : null}
            <span className="mx-auto font-mono text-cad-ink3">{i + 1} / {tabs.length}</span>
            {next ? <button type="button" className="cad-btn-box" onClick={() => setTab(next.id)}>{next.label} →</button> : null}
          </div>
        );
      })() : null}
    </div>
    </CadFormContext.Provider>
  );
}
